<template>
  <div class="ds-card-wrapper" :class="{ 'is-centered': centered }" ref="wrapper">
    <!-- Reserve scaled height so layout doesn't collapse -->
    <div class="stage" :style="{ height: scaledHeight + 'px' }">
      <div class="scale-canvas" ref="canvas" :style="{ transform: `scale(${scale})` }">
        <!-- ORIGINAL CARD CONTENT (unchanged) -->
        <div slot="pdf-content" ref="pdfDownload" id="pdfDownload" class="ds-card" :style="{
          '--card-width': width,
          '--band-height': bandHeight,
          '--band-color': bandColor,
        }">
          <img class="ds-card__map" src="@/assets/img/worldmap.svg" alt="World map" />

          <svg class="ds-card__band" viewBox="0 0 1000 200" preserveAspectRatio="none" aria-hidden="true">
            <path :fill="bandColor" d="M0 0 H1200 V45 C 700 -20, 220 60, 0 130 Z" />
          </svg>

          <div class="ds-card__logo-wrap">
            <img class="ds-card__logo" src="../assets/img/DIS-Logo.png" alt="logo" />
          </div>

          <div class="ds-card_info-wrapper">
            <div class="grading-label">Jewelry Grading Report</div>

            <div v-if="certImage" class="cert-image">
              <div style="color: red">ELECTRONIC COPY</div>
              <RemoveWhiteBG :src="certImage" :threshold="246" :softness="14" :chromaTolerance="20"
                :maxRenderWidth="900" width="70%" />
            </div>
            <div v-else style="color: red" class="spinner"></div>

            <div class="cert-info">
              <div class="cert-num-date">
                <div class="cert-num">DIS No. {{ certificate.CertNum }}</div>
                <div class="cert-date">Date: {{ CertDate }}</div>
              </div>

              <div class="cert-details">
                <div v-if="certificate.ColoredStone" class="info">
                  Metal: {{ certificate.Metal }}
                </div>
                <div class="info">Shape: {{ handleShapes(certificate) }}</div>
                <div class="info">
                  Carat: {{ handleWeight(certificate) }} ct. tw.
                </div>
                <div class="info">Color: {{ handleColor(certificate) }}</div>
                <div class="info">
                  Clarity: {{ handleClarity(certificate) }}
                </div>
              </div>

              <div class="description-wrapper">
                <label> Description </label>
                <div class="description">{{ certificate.CardDescription }}</div>
              </div>

              <div class="comments-wrapper">
                <label> Comments </label>
                <div class="comments">
                  Diamond weight estimated. Graded as mounting permits.
                </div>
              </div>
            </div>

            <div class="qr-code">
              <div class="top qr-label">Scan Here</div>
              <vue-qr backgroundColor="#fff0" class="qr_code" :size="130"
                :text="`https://certificates.diamondservicesusa.com/jewelry/${certificate.CertNum}`" />
              <div class="bottom qr-label">Digital Certificate</div>
            </div>
          </div>

          <div class="ds-card__bottom-left">
            <div class="ds-card__url">www.diamondservicesusa.com</div>
            <div class="ds-card__caption">
              See supplementary brochure for terms and conditions.
            </div>
          </div>
        </div>
        <!-- / ORIGINAL CARD CONTENT -->
      </div>
    </div>

    <div class="print-button">
      <button class="search" @click="downloadPDF">Download PDF</button>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import VueQr from "vue-qr";
import RemoveWhiteBG from "@/components/RemoveWhiteBG.vue";
import { DateTime } from "luxon";

export default {
  name: "DsMapCard",
  components: { RemoveWhiteBG, VueQr },
  props: {
    width: { type: String, default: "640px" }, // design width (used for initial measure only)
    centered: { type: Boolean, default: true },
    bandColor: { type: String, default: "#0c1733" },
    bandHeight: { type: String, default: "35%" },
    logoSrc: { type: String, default: "" },
    mapSrc: { type: String, default: "" },
    certificate: { type: Object, default: Object },
    certImage: { type: String, default: null },
  },
  data() {
    return {
      scale: 1,
      scaledHeight: 0, // actual reserved height after scaling
      baseW: 640, // natural card width (auto-detected)
      baseH: 400, // natural card height (auto-detected)
      ro: null,
    };
  },
  computed: {
    CertDate() {
      return DateTime.fromMillis(this.certificate.created).toFormat(
        "MM/dd/yyyy"
      );
    },
  },
  mounted() {
    // If width prop is numeric, prefer it for first pass
    const n = parseInt(this.width, 10);
    if (!isNaN(n)) this.baseW = n;

    this.$nextTick(async () => {
      // wait a beat for fonts/images
      await new Promise((r) => setTimeout(r, 50));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (err) {
          console.log(err);
        }
      }

      this.detectNaturalSize();
      this.measure();

      // re-measure on container resize / rotation
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.wrapper);

      // in case inner images alter size later
      for (const img of this.$el.querySelectorAll("img")) {
        if (!img.complete)
          img.addEventListener(
            "load",
            () => {
              this.detectNaturalSize();
              this.measure();
            },
            { once: true }
          );
      }
    });
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
  },
  unmounted() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    detectNaturalSize() {
      // temporarily remove scaling to read real size
      const canvas = this.$refs.canvas;
      const prev = canvas.style.transform;
      canvas.style.transform = "none";
      const card = this.$refs.pdfDownload;
      const el = card;
      const naturalW = el.scrollWidth || el.offsetWidth;
      const naturalH = el.scrollHeight || el.offsetHeight;
      if (naturalW) this.baseW = naturalW;
      if (naturalH) this.baseH = naturalH;
      canvas.style.transform = prev;
    },
    measure() {
      const wrapperW = this.$refs.wrapper.clientWidth;
      const s = Math.min(1, wrapperW / this.baseW); // never upscale beyond 1
      this.scale = s;
      this.scaledHeight = Math.ceil(this.baseH * s);
    },

    async downloadPDF() {
      const el = this.$refs.pdfDownload;
      if (!el) return;

      // 1) Wait for fonts/canvases/QR to finish
      await this.$nextTick();
      await new Promise((r) => setTimeout(r, 100));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          console.log({ e });
        }
      }

      // 2) Convert any CANVAS in the live card (QR, RemoveWhiteBG) to IMG in a cloned node
      const clone = el.cloneNode(true);
      const liveCanvases = el.querySelectorAll("canvas");
      const cloneCanvases = clone.querySelectorAll("canvas");
      liveCanvases.forEach((cv, i) => {
        try {
          const dataURL = cv.toDataURL("image/png");
          const img = new Image();
          img.src = dataURL;
          img.style.width = cv.style.width || (cv.width ? `${cv.width}px` : "");
          img.style.height =
            cv.style.height || (cv.height ? `${cv.height}px` : "");
          const target = cloneCanvases[i];
          if (target && target.parentNode)
            target.parentNode.replaceChild(img, target);
        } catch (e) {
          // tainted canvas? leave it and html2canvas will rasterize it anyway
        }
      });

      // 3) Put the clone off-screen so html2canvas can snapshot it exactly as styled
      const stage = document.createElement("div");
      stage.style.cssText = `
      position: fixed; left: -99999px; top: -99999px; background: #fff;`;
      stage.appendChild(clone);
      document.body.appendChild(stage);

      // 4) Snapshot the element at high DPI
      const canvas = await html2canvas(clone, {
        backgroundColor: "#ffffff",
        scale: 3, // 2–3 gives sharp text
        useCORS: true,
        logging: false,
        removeContainer: true,
      });

      // clean up the stage
      stage.remove();

      // 5) Build a Letter-landscape PDF in PX units and center the image
      const pageW = 1056; // 11in * 96
      const pageH = 816; // 8.5in * 96
      const pdf = new jsPDF({
        unit: "px",
        format: [pageW, pageH],
        orientation: "landscape",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const imgW = canvas.width;
      const imgH = canvas.height;

      // Fit image inside page while preserving aspect
      const maxW = pageW / 2;
      const maxH = pageH / 2;
      const scale = Math.min(maxW / imgW, maxH / imgH, 1);
      const drawW = imgW * scale;
      const drawH = imgH * scale;

      const x = (pageW - drawW) / 2;
      const y = (pageH - drawH) / 2;

      pdf.addImage(imgData, "JPEG", x, y, drawW, drawH);
      pdf.save(`Certificate #${this.certificate.CertNum}.pdf`);
    },

    handleWeight(item) {
      if (item.ColoredStone) {
        if (item.MainStoneWeight && item.SideStoneWeight) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }
        return item.MainStoneWeight || item.SideStoneWeight || "";
      }

      if (item.MainStoneType && item.MainStoneType.includes("Diamond Accent")) {
        return "Diamond Accent";
      }

      if (item.CertType) {
        if (
          !isNaN(Number(item.MainStoneWeight)) &&
          !isNaN(Number(item.SideStoneWeight))
        ) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }

        if (!isNaN(parseInt(item.MainStoneWeight))) {
          return item.MainStoneWeight;
        }

        if (!isNaN(parseInt(item.SideStoneWeight))) {
          return item.SideStoneWeight;
        }
      } else {
        if (
          item.HasSideStones &&
          !isNaN(Number(item.MainStoneWeight)) &&
          !isNaN(Number(item.SideStoneWeight))
        ) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }

        if (!isNaN(parseInt(item.MainStoneWeight))) {
          return item.MainStoneWeight;
        }

        if (item.HasSideStones && !isNaN(parseInt(item.SideStoneWeight))) {
          return item.SideStoneWeight;
        }
      }

      return "";
    },

    handleShapes(item) {
      if (item.MainStoneShape && item.SideStoneShape) {
        if (item.MainStoneShape === item.SideStoneShape) {
          return item.MainStoneShape;
        } else {
          return `${item.MainStoneShape.replace(
            /\b(?:Brilliant|Modified)\b/gi,
            ""
          ).trim()} / ${item.SideStoneShape.replace(
            /\b(?:Brilliant|Modified)\b/gi,
            ""
          ).trim()}`;
        }
      } else {
        return item.MainStoneShape || item.SideStoneShape;
      }
    },

    handleClarity(item) {
      if (item.ColoredStone) {
        if (
          item.MainStoneType &&
          !item.MainStoneType.includes("Diamond") &&
          !item.SideStoneType.includes("Diamond")
        )
          return "";
        return `${item.MainStoneType && item.MainStoneType.includes("Diamond")
            ? item.MainStoneClarity2.value
              ? `${item.MainStoneClarity.value} - ${item.MainStoneClarity2.value} `
              : item.MainStoneClarity.value
            : "*"
          } / ${item.SideStoneType && item.SideStoneType.includes("Diamond")
            ? item.SideStoneClarity.value
            : "*"
          }`;
      }
      if (item.HasSideStones) {
        if (item.MainStoneClarity.value === item.SideStoneClarity.value) {
          return item.MainStoneClarity.value;
        } else {
          return `${item.MainStoneClarity.value} ${item.SideStoneClarity.value
              ? "/ " + item.SideStoneClarity.value
              : "/ *"
            }`;
        }
      }

      if (item.MainStoneClarity.value && item.MainStoneClarity2.value) {
        return `${item.MainStoneClarity.value} - ${item.MainStoneClarity2.value}`;
      }

      return item.MainStoneClarity.value || item.SideStoneClarity.value;
    },

    handleColor(item) {
      const fancyColorsAbbr = {
        FP: "Fancy Pink",
        FIP: "Fancy Intense Pink",
        FVP: "Fancy Vivid Pink",
        FB: "Fancy Black",
        FIB: "Fancy Intense Black",
        FVB: "Fancy Vivid Black",
      };
      if (item.ColoredStone) {
        let color = item.MainStoneType.includes("Diamond")
          ? item.MainStoneColor.value
          : item.MainStoneType;

        if(item.MainStoneColor2.value) {
            color = color + " - " + item.MainStoneColor2.value;
        }

        if (item.SideStoneType) {
          if (item.SideStoneType.includes("Diamond")) {
            color = color + " / " + item.SideStoneColor.value;
          } else if (item.SideStoneType !== item.MainStoneType) {
            color = color + " / " + item.SideStoneType;
          }
        }

        return color;
      }

      if (item.HasSideStones) {
        if (item.MainStoneColor.value === item.SideStoneColor.value) {
          return (
            fancyColorsAbbr[item.MainStoneColor.value] ||
            item.MainStoneColor.value
          );
        } else {
          return `${fancyColorsAbbr[item.MainStoneColor.value] ||
            item.MainStoneColor.value
            } / ${fancyColorsAbbr[item.SideStoneColor.value] ||
            item.SideStoneColor.value
            }`;
        }
      } else if (item.MainStoneColor2.value) {
        return `${fancyColorsAbbr[item.MainStoneColor.value] ||
          item.MainStoneColor.value
          } - ${fancyColorsAbbr[item.MainStoneColor2.value] ||
          item.MainStoneColor2.value
          }`;
      } else {
        return (
          fancyColorsAbbr[item.MainStoneColor.value] ||
          item.MainStoneColor.value ||
          fancyColorsAbbr[item.SideStoneColor.value] ||
          item.SideStoneColor.value
        );
      }
    },

    handleCut(item) {
      if (item.ColoredStone) return "";
      if (item.MainStoneCut && item.SideStoneCut)
        return `${item.MainStoneCut} - ${item.SideStoneCut}`;
      else return item.MainStoneCut;
    },
  },
};
</script>

<style scoped>
/* Center the whole thing nicely */
.ds-card-wrapper {
  width: 100%;
  display: grid;
  justify-items: center;
  row-gap: 12px;
  font-weight: 600;
}

.ds-card-wrapper.is-centered {
  justify-items: center;
}

/* Reserve the exact scaled height */
.stage {
  width: 100%;
}

/* The scaler applies to the whole card */
.scale-canvas {
  transform-origin: top center;
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
  will-change: transform;
}

/* Base card (keeps your design ratio) */
.ds-card {
  --ratio: calc(85.6 / 54);
  width: var(--card-width, 640px);
  aspect-ratio: var(--ratio);
  position: relative;
  overflow: hidden;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);
  isolation: isolate;
  border: 1px solid #000;
}

/* Map layer */
.ds-card__map {
  position: absolute;
  width: 100%;
  height: 81%;
  top: 17%;
  object-fit: cover;
  filter: grayscale(1) contrast(0.85) brightness(1.15);
  opacity: 0.15;
  pointer-events: none;
  user-select: none;
  z-index: 1;
  transform: scale(0.9);
}

/* Band + logo */
.ds-card__band {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--band-height, 28%);
  z-index: 3;
}

.ds-card__logo-wrap {
  position: absolute;
  top: 10px;
  left: 14px;
  z-index: 4;
  display: flex;
  align-items: center;
  height: 50px;
}

.ds-card__logo {
  height: 80%;
  object-fit: contain;
}

/* Info layer */
.ds-card_info-wrapper {
  position: relative;
  z-index: 5;
  width: 100%;
  height: 100%;
  color: #000;
}

.grading-label {
  position: absolute;
  right: 1rem;
  top: 2rem;
}

.cert-info {
  width: 65%;
  height: 87%;
  position: absolute;
  top: 3rem;
  right: 0;
}

.cert-num-date {
  height: 49px;
}

.info {
  margin-bottom: 0.1rem;
}

.cert-image {
  position: absolute;
  width: 35%;
  height: 62%;
  top: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.cert-image img {
  width: 100%;
}

.ds-card__bottom-left {
  position: absolute;
  left: 16px;
  bottom: 1rem;
  z-index: 6;
  color: #2a2a2a;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Inter, Arial,
    sans-serif;
}

.ds-card__url {
  font-weight: 700;
  font-size: 9px;
  line-height: 1.2;
}

.ds-card__caption {
  font-size: 8px;
  opacity: 0.85;
  margin-top: 2px;
}

.qr-code {
  position: absolute;
  right: 3rem;
  top: 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-label {
  position: absolute;
  width: max-content;
  text-decoration: underline;
}

.top {
  top: -5px;
}

.bottom {
  bottom: -4px;
}

.comments-wrapper {
  position: absolute;
  bottom: 10px;
}

.description-wrapper {
  position: absolute;
  bottom: 75px;
}

.description {
  height: 90px;
  padding-right: 1rem;
}

/* Button */
.print-button {
  width: 100%;
  display: flex;
  justify-content: center;
}

.search {
  width: min(480px, 92%);
  padding: 14px 18px;
  border: 0;
  border-radius: 999px;
  font-weight: 800;
  font-size: clamp(14px, 4vw, 18px);
  background: #2f6df6;
  color: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
}

.search:active {
  transform: translateY(1px);
}

/* Remove old fixed scaling hacks */
@media (max-width: 480px) {
  .ds-card-wrapper {
    transform: none;
  }
}

@media (max-width: 700px) and (height < 400px) {
  .ds-card-wrapper {
    transform: none;
  }
}
</style>
